import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { WindowRef } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { BREAKPOINT, LayoutBreakPoints, LayoutConfig } from '../config/layout-config';

/**
 * Сервис для работы с брейкпоинтами на сайте.
 *
 * Определяет различные размеры экранов, используемые в магазине.
 * Размеры экранов глобально настраиваются в соответствии с требованиями верстки.
 *
 * Использует mobile-first подход и синхронизирован с Sass $grid-breakpoints.
 * Все значения брейкпоинтов указывают на минимальную ширину (min-width).
 *
 * Конфигурация брейкпоинтов (совпадает с $grid-breakpoints в Sass):
 * - xxs: 0                    // 0-374px (min-width: 0px)
 * - xs: { min: 375 }         // 375-575px (min-width: 375px)
 * - sm: { min: 576 }         // 576-767px (min-width: 576px)
 * - md: { min: 768 }         // 768-1023px (min-width: 768px)
 * - lg: { min: 1024 }        // 1024-1279px (min-width: 1024px)
 * - xl: { min: 1280 }        // 1280-1439px (min-width: 1280px)
 * - xxl: { min: 1440 }        // 1440x+ (min-width: 1440px)
 */
@Injectable({
  providedIn: 'root',
})
export class BreakpointService {
  private _breakpoints: BREAKPOINT[];

  breakpoint$: Observable<BREAKPOINT> = isPlatformBrowser(this.platform)
    ? this.winRef.resize$.pipe(
        map((event) => this.getBreakpoint((<Window>event.target).innerWidth)),
        distinctUntilChanged(),
      )
    : of(this.fallbackBreakpoint);

  constructor(
    protected winRef: WindowRef,
    protected layoutConfig: LayoutConfig,
    @Inject(PLATFORM_ID) protected platform: any,
  ) {}

  /**
   * Возвращает брейкпоинты для верстки магазина.
   *
   * Брейкпоинты берутся из LayoutConfig.breakpoints и сортируются по размеру.
   * Порядок сортировки: от маленьких экранов к большим (mobile-first).
   */
  get breakpoints(): BREAKPOINT[] {
    if (!this._breakpoints) {
      this._breakpoints = this.resolveBreakpointsFromConfig();
    }
    return this._breakpoints;
  }

  /**
   * Возвращает верхнюю границу (не включая) для брейкпоинта.
   *
   * Для всех брейкпоинтов кроме последнего: (min следующего - 1)
   * Для последнего брейкпоинта: 99999
   *
   * Используется в `getBreakpoint()` для сравнения windowWidth < size.
   */
  getSize(breakpoint: BREAKPOINT): number {
    return this.getMaxSize(breakpoint);
  }

  /**
   * Указывает, меньше ли текущий размер экрана, чем максимальный размер заданного брейкпоинта (включая сам брейкпоинт).
   * Включает все брейкпоинты ДО заданного включительно.
   */
  isDown(breakpoint: BREAKPOINT): Observable<boolean> {
    return this.breakpoint$.pipe(
      map((br) => this.breakpoints.slice(0, this.breakpoints.indexOf(breakpoint) + 1).includes(br)),
    );
  }

  /**
   * Указывает, больше ли текущий размер экрана, чем минимальный размер заданного брейкпоинта (включая сам брейкпоинт).
   * Включает заданный брейкпоинт и все ПОСЛЕ него.
   *
   * isUp(BREAKPOINT.md) вернет true для экранов 768px+.
   */
  isUp(breakpoint: BREAKPOINT): Observable<boolean> {
    return this.breakpoint$.pipe(
      map((br) => this.breakpoints.slice(this.breakpoints.indexOf(breakpoint)).includes(br)),
    );
  }

  /**
   * Указывает, соответствует ли заданный брейкпоинт текущему размеру экрана.
   *
   * isEqual(BREAKPOINT.md) вернет true только для экранов 768-1023px.
   */
  isEqual(breakpoint: BREAKPOINT): Observable<boolean> {
    return this.breakpoint$.pipe(map((br) => br === breakpoint));
  }

  /**
   * Возвращает fallback брейкпоинт, если нельзя определить актуальный.
   * Обычно это происходит на SSR без реального окна браузера.
   *
   * Возвращает наименьший размер экрана (mobile first).
   */
  protected get fallbackBreakpoint(): BREAKPOINT {
    return this.breakpoints?.[0];
  }

  /**
   * Резолвит брейкпоинты из конфигурации и сортирует их по размеру.
   *
   * Порядок сортировки: от маленьких экранов к большим (mobile-first).
   */
  protected resolveBreakpointsFromConfig(): BREAKPOINT[] {
    const sortByScreenSize = (next: BREAKPOINT, prev: BREAKPOINT): number => {
      const maxNext = Math.max(this.getMinSize(next) + 1 || 0, this.getMaxSize(next) || 0);
      const maxPrev = Math.max(this.getMinSize(prev) + 1 || 0, this.getMaxSize(prev) || 0);
      return maxNext < maxPrev ? -1 : 0;
    };
    return (Object.keys(this.config) as BREAKPOINT[]).sort(sortByScreenSize);
  }

  /**
   * Возвращает максимальный размер (верхнюю границу, не включая) для брейкпоинта.
   *
   * Всегда вычисляется как: min следующего брейкпоинта - 1.
   * Для последнего брейкпоинта возвращает 999999
   *
   * Примечание: В нашей системе все брейкпоинты задаются через min значения,
   * max вычисляется автоматически для обеспечения плотных диапазонов.
   */
  protected getMaxSize(breakpoint: BREAKPOINT): number {
    const breakpointConfig = this.config[breakpoint];

    if (!breakpointConfig) {
      return null;
    }

    // Находим следующий брейкпоинт в порядке возрастания
    const index = this.breakpoints.indexOf(breakpoint);
    const next = this.breakpoints[index + 1];

    // Если это последний брейкпоинт возвращаем 99999
    if (!next) {
      return 99999;
    }

    // max = min следующего брейкпоинта - 1
    return this.getMinSize(next) - 1;
  }

  /**
   * Возвращает минимальный размер (нижнюю границу, включая) для брейкпоинта.
   *
   * Обрабатывает два формата конфигурации:
   * 1. Число (xxs: 0) → возвращает число как min
   * 2. Объект с min (xs: { min: 375 }) → возвращает min
   *
   * Возвращает 0, если брейкпоинт не сконфигурирован.
   *
   * Примеры:
   * - xxs: 0 - min = 0
   * - xs: { min: 375 } - min = 375
   * - Несуществующий брейкпоинт - min = 0
   */
  protected getMinSize(breakpoint: BREAKPOINT): number {
    const config = this.config[breakpoint];

    if (typeof config === 'number') {
      return config;
    }

    return config?.min || 0;
  }

  /**
   * Возвращает брейкпоинт для заданной ширины окна.
   *
   * Реализует mobile-first логику. Находит наибольший брейкпоинт, чья минимальная ширина меньше или равна ширине окна.
   */
  protected getBreakpoint(windowWidth: number): BREAKPOINT {
    let result = this.breakpoints[0]; // xxs

    for (const breakpoint of this.breakpoints) {
      const minSize = this.getMinSize(breakpoint);
      if (windowWidth >= minSize) {
        result = breakpoint;
      } else {
        break; // Следующие имеют min больше
      }
    }

    return result;
  }

  /**
   * Вспомогательный метод для получения конфигурации брейкпоинтов.
   */
  protected get config(): LayoutBreakPoints {
    return this.layoutConfig?.breakpoints || {};
  }
}
